import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import { Timer, ArrowsClockwise, SignOut } from "@phosphor-icons/react";

// Gentle end-of-shift notice: the signed-in session lasts 8h; warn 15 min out.
const WARN_BEFORE_MS = 15 * 60 * 1000;
const TICK_MS = 30 * 1000;

export default function SessionExpiryBanner() {
  const { user, sessionExpiresAt, extendSession, logout } = useAuth();
  const navigate = useNavigate();
  const [remainingMs, setRemainingMs] = useState(null);
  const [snoozedUntil, setSnoozedUntil] = useState(0);
  const [busy, setBusy] = useState(false);

  const isAuthed = !!(user && user.id) && !!sessionExpiresAt;

  useEffect(() => {
    if (!isAuthed) { setRemainingMs(null); return undefined; }
    const tick = () => setRemainingMs(new Date(sessionExpiresAt).getTime() - Date.now());
    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [isAuthed, sessionExpiresAt]);

  const stay = useCallback(async () => {
    setBusy(true);
    try {
      await extendSession();
      toast.success("You're signed in for another 8 hours");
    } catch {
      toast.error("Couldn't extend your session — please sign in again");
      await logout();
      navigate("/login", { replace: true });
    } finally { setBusy(false); }
  }, [extendSession, logout, navigate]);

  const signOut = useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  if (!isAuthed || remainingMs == null || remainingMs > WARN_BEFORE_MS) return null;
  const expired = remainingMs <= 0;
  if (!expired && Date.now() < snoozedUntil) return null;

  const mins = Math.max(1, Math.ceil(remainingMs / 60000));

  return (
    <div className="fixed inset-x-0 bottom-4 z-[70] flex justify-center px-4 pointer-events-none"
      data-testid="session-expiry-banner" role="status" aria-live="polite">
      <div className="pointer-events-auto flex flex-wrap items-center gap-3 rounded-2xl bg-[#0A0A0A] text-white shadow-2xl border border-white/10 px-4 py-3 max-w-2xl w-full animate-in slide-in-from-bottom-4 fade-in duration-300">
        <span className="grid place-items-center h-9 w-9 rounded-full bg-[#0A84FF]/20 text-[#5AAAFF] shrink-0">
          <Timer size={20} weight="bold" />
        </span>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/50">End of shift</div>
          <div className="text-sm font-medium" data-testid="session-expiry-text">
            {expired ? "Your session has ended." : `Your session ends in ${mins} minute${mins === 1 ? "" : "s"}.`}
            <span className="text-white/60"> Stay signed in to keep working without interruption.</span>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {!expired && (
            <button type="button" onClick={() => setSnoozedUntil(Date.now() + 5 * 60 * 1000)}
              data-testid="session-expiry-dismiss"
              className="text-xs font-semibold text-white/60 hover:text-white px-2 py-2 transition-colors">
              Later
            </button>
          )}
          <button type="button" onClick={signOut} data-testid="session-expiry-signout"
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full border border-white/15 hover:bg-white/10 transition-colors">
            <SignOut size={14} weight="bold" /> Sign out
          </button>
          <button type="button" onClick={stay} disabled={busy} data-testid="session-expiry-stay"
            className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full bg-[#0A84FF] hover:bg-[#0070E0] disabled:opacity-60 transition-colors">
            <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} /> Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
